import { AppliedProductId, CountryName, PaymentSource } from '../types';
// performs {...current, ...updates} recursively
export function deepMergeObject(current, updates) {
    if (!current || !updates)
        return current !== null && current !== void 0 ? current : updates;
    current = Object.assign({}, current);
    for (const key of Object.keys(updates)) {
        if (!current.hasOwnProperty(key) || typeof updates[key] !== 'object')
            current[key] = updates[key];
        else
            current[key] = deepMergeObject(current[key], updates[key]);
    }
    return current;
}
export function deepEquals(a, b) {
    if (!a !== !b)
        return false;
    if (Array.isArray(a) !== Array.isArray(b))
        return false;
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length)
            return false;
        return a.every((x, i) => deepEquals(x, b[i]));
    }
    if (typeof a !== typeof b)
        return false;
    if (typeof a === 'object') {
        if (!a)
            return !b;
        const keys = Object.keys(a);
        if (!deepEquals(keys, Object.keys(b)))
            return false;
        return keys.every((k) => deepEquals(a[k], b[k]));
    }
    return a === b;
}
/**
 * Returns a hash code for a string.
 * (Compatible to Java's String.hashCode())
 *
 * The hash code for a string object is computed as
 *     s[0]*31^(n-1) + s[1]*31^(n-2) + ... + s[n-1]
 * using number arithmetic, where s[i] is the i th character
 * of the given string, n is the length of the string,
 * and ^ indicates exponentiation.
 * (The hash value of the empty string is zero.)
 *
 * from https://gist.github.com/hyamamoto/fd435505d29ebfa3d9716fd2be8d42f0
 *
 * @param {string} s a string
 * @return {number} a hash code value for the given string.
 */
export const hashCode = (str) => [...str].reduce((s, c) => (Math.imul(31, s) + c.charCodeAt(0)) | 0, 0);
export const getSessionId = (jwtClaims) => {
    return `${hashCode(`${jwtClaims.sub || jwtClaims.client_id}|${jwtClaims.auth_time || jwtClaims.exp || jwtClaims.nbf}`)}`;
};
// will only call `func` if the returned function has not been called with a different `newValue` in the past `time` milliseconds
export const debounceAsyncCall = (func, time = 750) => {
    let lastRef = undefined;
    let lastValue = undefined;
    let lastResult;
    return (value) => {
        if (value === lastValue)
            return Promise.resolve(lastResult);
        if (lastRef)
            clearTimeout(lastRef);
        return new Promise((resolve) => {
            lastRef = setTimeout(() => resolve(func(value).then((result) => {
                lastValue = value;
                return (lastResult = result);
            })), time);
        });
    };
};
// determines if a payment was written back to epic (not whether this writeback was successful)
export const wasEpicWritebackAttempted = (payment) => {
    switch (payment.source) {
        case PaymentSource.hpp:
            return !!payment.epicClientId;
        case PaymentSource.platform:
            return payment.appliedProductId === AppliedProductId.Epic || payment.appliedProductId === AppliedProductId.Csr24;
        case PaymentSource.portal:
            return false;
        case PaymentSource.api:
            return false; // not used currently
        case PaymentSource.lockbox:
        case PaymentSource.eLockbox:
            return false; // not implemented yet
        case PaymentSource.autopay:
            return true;
        case null:
            return false;
    }
};
export const formatAddress = (address) => {
    if (!address)
        return;
    const { address2, country, province, state, postalCode, zipCode, streetLine, city } = Object.fromEntries(Object.entries(address).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]));
    const unit = address2 ? (isNaN(Number(address2)) ? `${address2}` : `#${address2}`) : '';
    const stateOrProvince = country === CountryName.CA ? province : state;
    const zipOrPostal = country === CountryName.CA ? postalCode : zipCode;
    const addressLine1 = [streetLine, unit].filter(Boolean).join(' ');
    const addressLine2 = [city, stateOrProvince, zipOrPostal].filter(Boolean).join(', ');
    return Object.assign(Object.assign({}, (addressLine1 && { addressLine1 })), (addressLine2 && { addressLine2 }));
};
export const getConfigCore = (apiEnvVarName) => {
    var _a, _b, _c;
    const uiName = 'REACT_APP_' + apiEnvVarName;
    const value = (_c = (_a = process.env[apiEnvVarName]) !== null && _a !== void 0 ? _a : (_b = window === null || window === void 0 ? void 0 : window.env) === null || _b === void 0 ? void 0 : _b[uiName]) !== null && _c !== void 0 ? _c : process.env[uiName];
    if (value === undefined)
        console.error('missing env var ' + apiEnvVarName);
    return value;
};
export const getConfigCoreNum = (apiEnvVarName) => {
    const str = getConfigCore(apiEnvVarName);
    const value = Number(str);
    if (Number.isNaN(value))
        console.error(`missing env var (bad number format: '${str}') for: ${apiEnvVarName}`);
    return value;
};
export const getConfigCoreBool = (apiEnvVarName) => {
    var _a;
    const str = (_a = getConfigCore(apiEnvVarName)) === null || _a === void 0 ? void 0 : _a.toLowerCase().trim();
    if (str !== 'true' && str !== 'false')
        console.error(`missing env var (bad bool format: '${str}') for: ${apiEnvVarName}`);
    return str === 'true';
};
export const defaultNotifications = {
    autoDismissConfig: {
        enabled: true,
        delayInSec: 7,
    },
};
export var AbortReasons;
(function (AbortReasons) {
    AbortReasons["TABLE_SCROLL"] = "table-scroll";
    AbortReasons["FILTER_CHANGE"] = "filter-change";
    AbortReasons["AUTH_CONTEXT_CHANGE"] = "auth-context-change";
})(AbortReasons || (AbortReasons = {}));
//# sourceMappingURL=misc.js.map